import { useState, useEffect, useCallback } from 'react';
import { favoritesApi } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// NOTE: Favorites are only stored in the database, not localStorage
// Unauthenticated users will see an empty favorites list

interface UseUserFavoritesReturn {
  favorites: string[];
  isLoading: boolean;
  isSyncing: boolean;
  addFavorite: (saleId: string) => Promise<boolean>;
  removeFavorite: (saleId: string) => Promise<boolean>;
  toggleFavorite: (saleId: string) => Promise<boolean>;
  isFavorite: (saleId: string) => boolean;
  clearFavorites: () => void;
  error: string | null;
}

export const useUserFavorites = (): UseUserFavoritesReturn => {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch favorites from API when user is authenticated
  const fetchDbFavorites = useCallback(async (userId: string) => {
    console.log('=== fetchDbFavorites called ===', userId);
    try {
      const data = await favoritesApi.getFavoriteIds(userId);
      console.log('getFavoriteIds result:', data);
      setFavorites(data || []);
    } catch (err: any) {
      console.error('Error fetching favorites:', err);
      setError(err.message || 'Failed to fetch favorites');
    }
  }, []);

  // Load favorites on mount - only from database for authenticated users
  useEffect(() => {
    const loadFavorites = () => {
      if (isAuthenticated && user?.id) {
        // User is logged in - fetch from database
        fetchDbFavorites(user.id).finally(() => setIsLoading(false));
      } else {
        // Not authenticated - no localStorage fallback, users must log in to see favorites
        setFavorites([]);
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, [isAuthenticated, user?.id, fetchDbFavorites]);

  // Re-fetch favorites when user logs in (auth state changes from false to true)
  // This handles the race condition where auth completes after initial mount
  useEffect(() => {
    if (isAuthenticated && user?.id && isLoading) {
      // Auth just became authenticated after initial load - fetch from DB
      fetchDbFavorites(user.id).finally(() => setIsLoading(false));
    }
  }, [isAuthenticated]);

  const addFavorite = useCallback(async (saleId: string): Promise<boolean> => {
    console.log('=== addFavorite called ===', { isAuthenticated, userId: user?.id, saleId });
    
    if (!isAuthenticated || !user?.id) {
      // User must be logged in to add favorites
      console.warn('Cannot add favorite: user not authenticated');
      return false;
    }

    try {
      await favoritesApi.addFavorite(user.id, saleId);

      setFavorites(prev => {
        if (prev.includes(saleId)) return prev;
        return [...prev, saleId];
      });
      return true;
    } catch (err: any) {
      console.error('Error adding favorite:', err);
      setError(err.message || 'Failed to add favorite');
      return false;
    }
  }, [isAuthenticated, user?.id]);

  const removeFavorite = useCallback(async (saleId: string): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) {
      // User must be logged in to remove favorites
      console.warn('Cannot remove favorite: user not authenticated');
      return false;
    }

    try {
      await favoritesApi.removeFavorite(user.id, saleId);

      setFavorites(prev => prev.filter(id => id !== saleId));
      return true;
    } catch (err: any) {
      console.error('Error removing favorite:', err);
      setError(err.message || 'Failed to remove favorite');
      return false;
    }
  }, [isAuthenticated, user?.id]);

  const toggleFavorite = useCallback(async (saleId: string): Promise<boolean> => {
    if (favorites.includes(saleId)) {
      return removeFavorite(saleId);
    } else {
      return addFavorite(saleId);
    }
  }, [favorites, addFavorite, removeFavorite]);

  const isFavorite = useCallback((saleId: string): boolean => {
    return favorites.includes(saleId);
  }, [favorites]);

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  return {
    favorites,
    isLoading,
    isSyncing,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    error,
  };
};

export default useUserFavorites;